import {
  createClient,
  type AuthChangeEvent,
  type Session,
  type SupabaseClient,
} from '@supabase/supabase-js';

/**
 * Session utilisateur — Supabase Auth cote navigateur.
 *
 * C'est la seule facon d'entrer dans l'application depuis l'etape 2 de
 * PLAN-WEB. Le jeton partage `VITE_APP_ACCESS_TOKEN` a disparu : un secret
 * compile dans le bundle ne protege rien des lors que le frontend est publie.
 *
 * Ce module ne connait que la session. Il n'appelle jamais /api : c'est
 * `client.ts` qui lui demande un jeton et le pose sur ses requetes.
 */

/**
 * URL du projet Supabase et cle **anonyme**.
 *
 * La cle anonyme est publique par construction : elle est presente dans le
 * bundle de toute application Supabase cote navigateur. Ce n'est pas une fuite,
 * a une condition qui est remplie ici — RLS est activee sur toutes les tables
 * du schema (voir server/src/db/schema.sql), et aucune politique n'ouvre quoi
 * que ce soit a un porteur anonyme. Sans RLS, cette cle laisserait lire la
 * bibliotheque entiere.
 */
const SUPABASE_URL = (import.meta.env.VITE_SUPABASE_URL ?? '').trim().replace(/\/+$/, '');
const SUPABASE_ANON_KEY = (import.meta.env.VITE_SUPABASE_ANON_KEY ?? '').trim();

/**
 * La connexion est-elle configurable sur cette machine ?
 *
 * Sans les deux variables, il n'y a pas d'ecran de connexion possible. On le
 * dit franchement plutot que d'afficher un formulaire qui ne peut pas marcher.
 */
export function isSessionConfigured(): boolean {
  return SUPABASE_URL.length > 0 && SUPABASE_ANON_KEY.length > 0;
}

/**
 * Client Supabase, cree une seule fois.
 *
 * `persistSession` et `autoRefreshToken` sont les defauts du SDK, ecrits ici
 * parce qu'ils portent tout le confort d'usage : la session survit au
 * rechargement de la page, et le jeton d'acces est renouvele en tache de fond
 * avant d'expirer. Sans eux, il faudrait se reconnecter toutes les heures.
 *
 * `detectSessionInUrl` est en revanche desactive a dessein : la connexion se
 * fait par mot de passe, il n'y a ni lien magique ni retour OAuth, donc rien a
 * lire dans le fragment d'URL.
 */
const supabase: SupabaseClient | null = isSessionConfigured()
  ? createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: false,
      },
    })
  : null;

export interface CompteConnecte {
  email: string | null;
}

function versCompte(session: Session | null): CompteConnecte | null {
  if (session === null) return null;
  return { email: session.user.email ?? null };
}

/**
 * Jeton d'acces courant, ou `null` si personne n'est connecte.
 *
 * `getSession()` rend le jeton en memoire et le renouvelle s'il est expire :
 * c'est lui qu'il faut appeler avant chaque requete, et non un jeton garde de
 * cote qui deviendrait perime au bout d'une heure.
 */
export async function getAccessToken(): Promise<string | null> {
  if (supabase === null) return null;

  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}

/** Compte connecte au demarrage, avant tout rendu. */
export async function getCompteCourant(): Promise<CompteConnecte | null> {
  if (supabase === null) return null;

  const { data } = await supabase.auth.getSession();
  return versCompte(data.session);
}

/**
 * Traduction des refus de Supabase.
 *
 * Le SDK repond en anglais, et son message le plus frequent
 * (« Invalid login credentials ») ne distingue volontairement pas l'e-mail
 * inconnu du mot de passe faux — c'est ce qui evite de confirmer l'existence
 * d'un compte a qui essaie des adresses. On garde cette prudence en francais.
 */
function messageDeRefus(message: string): string {
  const brut = message.toLowerCase();

  if (brut.includes('invalid login credentials')) {
    return 'Adresse e-mail ou mot de passe incorrect.';
  }
  if (brut.includes('email not confirmed')) {
    return "Ce compte n'a pas encore ete confirme. Verifie ta boite mail.";
  }
  if (brut.includes('too many requests') || brut.includes('rate limit')) {
    return 'Trop de tentatives. Patiente une minute avant de reessayer.';
  }
  if (brut.includes('failed to fetch') || brut.includes('network')) {
    return "Impossible de joindre le service d'authentification.";
  }

  return 'Connexion impossible pour le moment.';
}

export class SessionError extends Error {}

/** Connexion par e-mail et mot de passe. Leve une `SessionError` en cas de refus. */
export async function signIn(email: string, password: string): Promise<void> {
  if (supabase === null) {
    throw new SessionError("La connexion n'est pas configuree sur cette installation.");
  }

  const { error } = await supabase.auth.signInWithPassword({ email, password });
  if (error !== null) throw new SessionError(messageDeRefus(error.message));
}

/**
 * Deconnexion.
 *
 * `scope: 'local'` efface la session de ce navigateur sans invalider les autres
 * appareils. C'est aussi ce qui permet de se deconnecter quand le reseau est
 * coupe : une revocation cote serveur echouerait et laisserait l'utilisateur
 * coince sur un ecran auquel il n'a plus acces.
 */
export async function signOut(): Promise<void> {
  if (supabase === null) return;
  await supabase.auth.signOut({ scope: 'local' });
}

/**
 * S'abonne aux changements de session : connexion, deconnexion, et expiration
 * d'un jeton que le SDK n'a pas pu renouveler.
 *
 * Rend la fonction de desabonnement.
 */
export function onSessionChange(
  listener: (compte: CompteConnecte | null) => void,
): () => void {
  if (supabase === null) return () => undefined;

  const { data } = supabase.auth.onAuthStateChange(
    (_event: AuthChangeEvent, session: Session | null) => {
      listener(versCompte(session));
    },
  );

  return () => {
    data.subscription.unsubscribe();
  };
}
